import type { SearchQuotesQuery } from '@paintscout/api'
import type ApolloCache from '@wora/apollo-cache'
import gql from 'graphql-tag'

const CACHED_QUOTE_FLAGS_FRAGMENT = gql`
  fragment CachedQuoteFlags on Quote {
    _id
    trashed
    deleteDate
  }
`

export interface CachedQuoteFlags {
  _id: string
  trashed?: boolean | null
  deleteDate?: number | null
}

/**
 * Reads just the trashed/deleteDate flags off a cached Quote fragment. Returns null if the
 * fragment isn't in cache — that is the *expected* case for brand-new quotes or quotes the user
 * has never interacted with, so this silently swallows `readFragment` misses rather than logging.
 * Treat a null return as "unknown prior state".
 */
export function readCachedQuoteFlags(cache: ApolloCache, id: string): CachedQuoteFlags | null {
  try {
    const frag = cache.readFragment<CachedQuoteFlags>({
      id: `Quote:${id}`,
      fragment: CACHED_QUOTE_FLAGS_FRAGMENT
    })
    return frag ?? null
  } catch (_e) {
    // Expected on cache miss; see jsdoc above.
    return null
  }
}

/**
 * Pure decision: given the prior cache state and the outgoing save, is this a restore transition
 * that requires invalidating searchQuotes list caches?
 *
 * A restore happens when the quote was trashed (or had a deleteDate) AND the save makes it active
 * (neither trashed nor hard-deleted), OR when the caller explicitly sets `isRestored` (RecoveryNotice
 * does this even when we can't read a prior from cache, e.g. when the search fragment was never
 * loaded). Unknown prior state (null flags) alone must NOT trigger an eviction — otherwise every
 * save of a never-cached quote would needlessly nuke the list cache.
 */
export function isQuoteRestoreTransition(args: {
  priorTrashed?: boolean | null
  priorDeleteDate?: number | null
  nextTrashed?: boolean | null
  nextDeleted?: boolean | null
  isRestored?: boolean | null
}): boolean {
  const wasTrashed = !!(args.priorTrashed || args.priorDeleteDate)
  const isNowActive = !args.nextTrashed && !args.nextDeleted
  return !!args.isRestored || (wasTrashed && isNowActive)
}

/**
 * Pure transform used by `deleteQuotes` to strip deleted ids from every cached searchQuotes
 * result. `total_rows` is decremented by however many we actually removed from *this* cached
 * page — never below zero — because a given id may not appear in every cached variant.
 */
export function filterSearchQuotesByIds(data: SearchQuotesQuery, removeIds: string[]): SearchQuotesQuery {
  if (!data?.searchQuotes?.quotes) return data
  const removeSet = new Set(removeIds)
  const filtered = data.searchQuotes.quotes.filter((q) => !removeSet.has(q._id))
  const removed = data.searchQuotes.quotes.length - filtered.length
  return {
    ...data,
    searchQuotes: {
      ...data.searchQuotes,
      quotes: filtered,
      total_rows: Math.max(0, data.searchQuotes.total_rows - removed)
    }
  }
}
